import axios from 'axios';

// Dynamically determine API URL based on how the frontend is accessed
const getApiBase = () => {
  // Create React App exposes env vars via process.env (must be prefixed REACT_APP_)
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
  }
  // Local development fallback
  return 'http://localhost:5000/api';
};

export const API_BASE = getApiBase();

// A plain URL, not an axios call — used directly as an <img src>. The route
// is @login_required, so this relies on the same session cookie axios already
// sends (see withCredentials below); the browser attaches it to <img>
// requests the same way for a same-session cross-origin request.
export const getTeamLogoUrl = (participantId) =>
  participantId ? `${API_BASE}/odds/team-logo/${participantId}` : null;

// odds-api.io has zero logo coverage for esports (checked live), so esports
// teams go through a separate Liquipedia-backed lookup keyed by (game, team
// name) instead of a participant id — see /team-logo-esports on the backend.
export const getEsportsTeamLogoUrl = (gameTitle, teamName) =>
  gameTitle && teamName
    ? `${API_BASE}/odds/team-logo-esports?game=${encodeURIComponent(gameTitle)}&team=${encodeURIComponent(teamName)}`
    : null;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export const api = {
  // Auth
  login: async (username, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, { username, password });
    return response.data;
  },

  logout: async () => {
    const response = await axios.post(`${API_BASE}/auth/logout`);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await axios.get(`${API_BASE}/auth/me`);
    return response.data;
  },

  getMaintenanceStatus: async () => {
    const response = await axios.get(`${API_BASE}/auth/maintenance-status`);
    return response.data;
  },

  getWarning: async () => {
    const response = await axios.get(`${API_BASE}/auth/warning`);
    return response.data;
  },

  // Accounts
  createAccount: async (accountData) => {
    const response = await axios.post(`${API_BASE}/accounts/create`, accountData);
    return response.data;
  },

  getAccounts: async (role = null) => {
    const url = role ? `${API_BASE}/accounts/list?role=${role}` : `${API_BASE}/accounts/list`;
    const response = await axios.get(url);
    return response.data;
  },

  updateAccount: async (userId, updates) => {
    const response = await axios.put(`${API_BASE}/accounts/${userId}/update`, updates);
    return response.data;
  },

  getAccount: async (userId) => {
    const response = await axios.get(`${API_BASE}/accounts/${userId}`);
    return response.data;
  },

  // Reports
  getDashboard: async () => {
    const response = await axios.get(`${API_BASE}/reports/dashboard`);
    return response.data;
  },

  getWeeklyReport: async () => {
    const response = await axios.get(`${API_BASE}/reports/weekly`);
    return response.data;
  },

  // Bets
  placeBet: async (betData) => {
    const response = await axios.post(`${API_BASE}/bets/place`, betData);
    return response.data;
  },

  placeParlay: async (parlayData) => {
    const response = await axios.post(`${API_BASE}/bets/place-parlay`, parlayData);
    return response.data;
  },

  placeMultipleSingles: async (data) => {
    const response = await axios.post(`${API_BASE}/bets/place-multiple`, data);
    return response.data;
  },

  getBetHistory: async (userId = null, status = null, period = 'week') => {
    let url = `${API_BASE}/bets/history?include_parlays=true`;
    if (userId) url += `&user_id=${userId}`;
    if (status) url += `&status=${status}`;
    if (period) url += `&period=${period}`;
    const response = await axios.get(url);
    return response.data;
  },

  cancelBet: async (betId) => {
    const response = await axios.post(`${API_BASE}/bets/${betId}/cancel`);
    return response.data;
  },

  cancelParlay: async (parlayId) => {
    const response = await axios.post(`${API_BASE}/bets/parlay/${parlayId}/cancel`);
    return response.data;
  },

  // Favorites
  getFavoriteTeams: async () => {
    const response = await axios.get(`${API_BASE}/favorites/teams`);
    return response.data;
  },

  addFavoriteTeam: async (teamName) => {
    const response = await axios.post(`${API_BASE}/favorites/teams/add`, { team_name: teamName });
    return response.data;
  },

  removeFavoriteTeam: async (teamName) => {
    const response = await axios.post(`${API_BASE}/favorites/teams/remove`, { team_name: teamName });
    return response.data;
  },

  // Odds
  getGames: async (sport = null) => {
    const url = sport ? `${API_BASE}/odds/games?sport=${sport}` : `${API_BASE}/odds/games`;
    const response = await axios.get(url);
    return response.data;
  },

  getActiveLeagues: async (cat) => {
    const response = await axios.get(`${API_BASE}/odds/active-leagues?cat=${cat}`);
    return response.data;
  },

  getAllGames: async () => {
    const response = await axios.get(`${API_BASE}/odds/games/all`);
    return response.data;
  },

  getLiveGames: async () => {
    const response = await axios.get(`${API_BASE}/odds/games/live`);
    return response.data;
  },

  getLiveOddsSummary: async () => {
    const response = await axios.get(`${API_BASE}/odds/live-odds-summary`);
    return response.data;
  },

  getLiveOddsGames: async (sport) => {
    const response = await axios.get(`${API_BASE}/odds/live-games?sport=${encodeURIComponent(sport)}`);
    return response.data;
  },

  getEventGameMarkets: async (sportKey, eventId) => {
    const response = await axios.get(`${API_BASE}/odds/event/${eventId}/game-markets?sport=${sportKey}`);
    return response.data;
  },

  getEventPlayerProps: async (sportKey, eventId) => {
    const response = await axios.get(`${API_BASE}/odds/event/${eventId}/player-props?sport=${sportKey}`);
    return response.data;
  },

  getCustomerPerformance: async (period = 'week', sort = 'profit', order = 'desc') => {
    const response = await axios.get(`${API_BASE}/reports/customer-performance`, {
      params: { period, sort, order }
    });
    return response.data;
  },

  getAgentPerformance: async (period = 'week', sort = 'earnings', order = 'desc') => {
    const response = await axios.get(`${API_BASE}/reports/agent-performance`, {
      params: { period, sort, order }
    });
    return response.data;
  },

  getWeeklyBreakdown: async (weekOffset = 0) => {
    const response = await axios.get(`${API_BASE}/reports/weekly-breakdown`, { params: { week_offset: weekOffset } });
    return response.data;
  },

  getCustomerDetail: async (customerId, period = 'week') => {
    const response = await axios.get(`${API_BASE}/reports/customer-detail/${customerId}`, {
      params: { period }
    });
    return response.data;
  },

  // Messages
  sendMessage: async (subject, message, recipientId = null, toCs = false, toAdmin = false) => {
    const response = await axios.post(`${API_BASE}/messages/send`, {
      subject,
      message,
      recipient_id: recipientId,
      to_cs: toCs,
      to_admin: toAdmin
    });
    return response.data;
  },

  getMyBook: async () => {
    const response = await axios.get(`${API_BASE}/messages/my-book`);
    return response.data;
  },

  replyToMessage: async (messageId, message) => {
    const response = await axios.post(`${API_BASE}/messages/reply/${messageId}`, { message });
    return response.data;
  },

  getInbox: async () => {
    const response = await axios.get(`${API_BASE}/messages/inbox`);
    return response.data;
  },

  getSentMessages: async () => {
    const response = await axios.get(`${API_BASE}/messages/sent`);
    return response.data;
  },

  getMessage: async (messageId) => {
    const response = await axios.get(`${API_BASE}/messages/${messageId}`);
    return response.data;
  },

  markMessageRead: async (messageId) => {
    const response = await axios.post(`${API_BASE}/messages/${messageId}/mark-read`);
    return response.data;
  },

  resolveMessage: async (messageId) => {
    const response = await axios.post(`${API_BASE}/messages/${messageId}/resolve`);
    return response.data;
  },

  unresolveMessage: async (messageId) => {
    const response = await axios.post(`${API_BASE}/messages/${messageId}/unresolve`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await axios.get(`${API_BASE}/messages/unread-count`);
    return response.data;
  },

  // Settlement
  getPendingSettlement: async (checkCompletion = false) => {
    const url = checkCompletion
      ? `${API_BASE}/bets/pending-settlement?check_completion=true`
      : `${API_BASE}/bets/pending-settlement`;
    const response = await axios.get(url);
    return response.data;
  },

  autoSettleBets: async () => {
    const response = await axios.post(`${API_BASE}/bets/auto-settle`);
    return response.data;
  },

  getNeedsReview: async () => {
    const response = await axios.get(`${API_BASE}/bets/needs-review`);
    return response.data;
  },

  batchSettle: async (outcomes) => {
    const response = await axios.post(`${API_BASE}/bets/batch-settle`, { outcomes });
    return response.data;
  },

  refreshPendingScores: async () => {
    const response = await axios.post(`${API_BASE}/bets/refresh-pending-scores`);
    return response.data;
  },

  refreshSport: async (sportKey) => {
    const response = await axios.get(`${API_BASE}/odds/cache/clear/${sportKey}`);
    return response.data;
  },

  getFutures: async () => {
    const response = await axios.get(`${API_BASE}/odds/futures`);
    return response.data;
  },

  // Dashboard
  getGamesToWatch: async () => {
    const response = await axios.get(`${API_BASE}/bets/games-to-watch`);
    return response.data;
  },

  // Admin API
  admin: {
    getDashboard: async () => {
      const response = await axios.get(`${API_BASE}/admin/dashboard`);
      return response.data;
    },

    getBooks: async () => {
      const response = await axios.get(`${API_BASE}/admin/books`);
      return response.data;
    },

    createBook: async (bookData) => {
      const response = await axios.post(`${API_BASE}/admin/books`, bookData);
      return response.data;
    },

    getBook: async (bookId) => {
      const response = await axios.get(`${API_BASE}/admin/books/${bookId}`);
      return response.data;
    },

    updateBook: async (bookId, bookData) => {
      const response = await axios.put(`${API_BASE}/admin/books/${bookId}`, bookData);
      return response.data;
    },

    getBookBilling: async (bookId) => {
      const response = await axios.get(`${API_BASE}/admin/books/${bookId}/billing`);
      return response.data;
    },

    updateBookBilling: async (bookId, billingData) => {
      const response = await axios.put(`${API_BASE}/admin/books/${bookId}/billing`, billingData);
      return response.data;
    },

    updateBookSports: async (bookId, enabledSports) => {
      const response = await axios.put(`${API_BASE}/admin/books/${bookId}/sports`, { enabled_sports: enabledSports });
      return response.data;
    },

    getBookActiveUsers: async (bookId) => {
      const response = await axios.get(`${API_BASE}/admin/books/${bookId}/active-users`);
      return response.data;
    },

    getBillingPeriods: async () => {
      const response = await axios.get(`${API_BASE}/admin/billing/periods`);
      return response.data;
    },

    getCurrentBilling: async () => {
      const response = await axios.get(`${API_BASE}/admin/billing/current`);
      return response.data;
    },

    finalizeBilling: async () => {
      const response = await axios.post(`${API_BASE}/admin/billing/finalize`);
      return response.data;
    },

    getBillingHistoryAllBooks: async () => {
      const response = await axios.get(`${API_BASE}/admin/billing/history`);
      return response.data;
    },

    markBillingPaid: async (billingId) => {
      const response = await axios.post(`${API_BASE}/admin/billing/${billingId}/mark-paid`);
      return response.data;
    },

    markBillingUnpaid: async (billingId) => {
      const response = await axios.post(`${API_BASE}/admin/billing/${billingId}/mark-unpaid`);
      return response.data;
    },

    createAgent: async (agentData) => {
      const response = await axios.post(`${API_BASE}/admin/agents`, agentData);
      return response.data;
    },

    previewImport: async (file, bookId) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('book_id', bookId);
      const response = await axios.post(`${API_BASE}/admin/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },

    executeImport: async (file, bookId) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('book_id', bookId);
      const response = await axios.post(`${API_BASE}/admin/import/customers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },

    downloadDatabase: async () => {
      const response = await axios.get(`${API_BASE}/admin/export/database`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `betting_system_${new Date().toISOString().slice(0,10)}.db`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    exportJson: async () => {
      const response = await axios.get(`${API_BASE}/admin/export/json`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `betting_system_export_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    // Ticket Management
    listTickets: async (page = 1, perPage = 50, status = null, search = '') => {
      let url = `${API_BASE}/admin/tickets?page=${page}&per_page=${perPage}`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url);
      return response.data;
    },

    getTicket: async (ticketNumber) => {
      const response = await axios.get(`${API_BASE}/admin/tickets/${ticketNumber}`);
      return response.data;
    },

    deleteTicket: async (ticketNumber) => {
      const response = await axios.delete(`${API_BASE}/admin/tickets/${ticketNumber}/delete`);
      return response.data;
    },

    // Maintenance Mode
    getMaintenanceStatusAdmin: async () => {
      const response = await axios.get(`${API_BASE}/admin/maintenance`);
      return response.data;
    },

    startMaintenance: async (data = {}) => {
      const response = await axios.post(`${API_BASE}/admin/maintenance/start`, data);
      return response.data;
    },

    stopMaintenance: async () => {
      const response = await axios.post(`${API_BASE}/admin/maintenance/stop`);
      return response.data;
    },

    cancelScheduledMaintenance: async (maintenanceId) => {
      const response = await axios.post(`${API_BASE}/admin/maintenance/cancel-scheduled`, { maintenance_id: maintenanceId });
      return response.data;
    },

    // Site Warning
    getWarning: async () => {
      const response = await axios.get(`${API_BASE}/admin/warning`);
      return response.data;
    },

    setWarning: async (message) => {
      const response = await axios.post(`${API_BASE}/admin/warning`, { message });
      return response.data;
    },

    clearWarning: async () => {
      const response = await axios.delete(`${API_BASE}/admin/warning`);
      return response.data;
    },

    getPendingBetsByGame: async () => {
      const response = await axios.get(`${API_BASE}/admin/pending-bets-by-game`);
      return response.data;
    },

    voidGame: async (gameId, sportKey, reason) => {
      const response = await axios.post(`${API_BASE}/admin/void-game`, {
        game_id: gameId,
        sport_key: sportKey,
        reason,
      });
      return response.data;
    },

    getFraudFlagged: async () => {
      const response = await axios.get(`${API_BASE}/admin/live-fraud/flagged`);
      return response.data;
    },

    getFraudIncidents: async (userId) => {
      const response = await axios.get(`${API_BASE}/admin/live-fraud/incidents/${userId}`);
      return response.data;
    },

    reviewFraudFlag: async (flagId, action) => {
      const response = await axios.post(`${API_BASE}/admin/live-fraud/review/${flagId}`, { action });
      return response.data;
    },
  },

  // Ticket lookup for books/agents
  getTicketByNumber: async (ticketNumber) => {
    const response = await axios.get(`${API_BASE}/bets/ticket/${ticketNumber}`);
    return response.data;
  },

  deleteTicketByNumber: async (ticketNumber) => {
    const response = await axios.delete(`${API_BASE}/bets/ticket/${ticketNumber}/delete`);
    return response.data;
  },

  // Cashout — betType is 'single' | 'parlay' | 'live_single' | 'live_parlay'
  getCashoutStreamUrl: (betType, betId) => {
    const path = betType.startsWith('live_')
      ? `${API_BASE}/live/${betType === 'live_single' ? 'bet' : 'parlay'}/${betId}/cashout-stream`
      : `${API_BASE}/bets/${betType}/${betId}/cashout-stream`;
    return path;
  },

  executeCashout: async (betType, betId, offeredAmount) => {
    const path = betType.startsWith('live_')
      ? `${API_BASE}/live/${betType === 'live_single' ? 'bet' : 'parlay'}/${betId}/cashout`
      : `${API_BASE}/bets/${betType}/${betId}/cashout`;
    const response = await axios.post(path, { offered_amount: offeredAmount });
    return response.data;
  },

  // Cashout Configuration (for books)
  getCashoutConfig: async () => {
    const response = await axios.get(`${API_BASE}/accounts/cashout-config`);
    return response.data;
  },

  updateCashoutConfig: async (config) => {
    const response = await axios.put(`${API_BASE}/accounts/cashout-config`, config);
    return response.data;
  },

  getAgentPermissions: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agent-permissions/${agentId}`);
    return response.data;
  },

  updateAgentPermissions: async (agentId, data) => {
    const response = await axios.put(`${API_BASE}/agent-permissions/${agentId}`, data);
    return response.data;
  },

  // Book Billing (read-only for books)
  getMyCurrentBilling: async () => {
    const response = await axios.get(`${API_BASE}/accounts/my-billing/current`);
    return response.data;
  },

  getMyBillingHistory: async () => {
    const response = await axios.get(`${API_BASE}/accounts/my-billing/history`);
    return response.data;
  },

  getMyBookSports: async () => {
    const response = await axios.get(`${API_BASE}/accounts/my-book-sports`);
    return response.data;
  },

  updateMyBookSports: async (enabledSports) => {
    const response = await axios.put(`${API_BASE}/accounts/my-book-sports`, { enabled_sports: enabledSports });
    return response.data;
  },

  getMyAgentSports: async () => {
    const response = await axios.get(`${API_BASE}/accounts/my-agent-sports`);
    return response.data;
  },

  updateMyAgentSports: async (enabledSports) => {
    const response = await axios.put(`${API_BASE}/accounts/my-agent-sports`, { enabled_sports: enabledSports });
    return response.data;
  },

  getAgentDefaultTemplate: async () => {
    const response = await axios.get(`${API_BASE}/accounts/agent-default-template`);
    return response.data;
  },

  updateAgentDefaultTemplate: async (template) => {
    const response = await axios.put(`${API_BASE}/accounts/agent-default-template`, template);
    return response.data;
  },

  applyAgentDefaultTemplate: async () => {
    const response = await axios.post(`${API_BASE}/accounts/agent-default-template/apply-all`);
    return response.data;
  },

  getAccountTemplate: async (scope) => {
    const response = await axios.get(`${API_BASE}/accounts/account-template/${scope}`);
    return response.data;
  },

  updateAccountTemplate: async (scope, data) => {
    const response = await axios.put(`${API_BASE}/accounts/account-template/${scope}`, { data });
    return response.data;
  },

  applyAccountTemplate: async (scope, adjustments) => {
    const response = await axios.post(`${API_BASE}/accounts/account-template/${scope}/apply-all`, adjustments ? { adjustments } : {});
    return response.data;
  },

  updateCsForwarding: async (customerEnabled, agentEnabled) => {
    const response = await axios.put(`${API_BASE}/cs/books/cs-forwarding`, {
      cs_forwarding_enabled: customerEnabled,
      agent_cs_forwarding_enabled: agentEnabled
    });
    return response.data;
  },

  // Live betting
  getLiveSports: async () => {
    const response = await axios.get(`${API_BASE}/live/sports`);
    return response.data;
  },

  getLiveStreamUrl: (sport) =>
    `${API_BASE}/live/stream?sport=${encodeURIComponent(sport)}`,

  placeLiveBet: async (data) => {
    const response = await axios.post(`${API_BASE}/live/bet`, data);
    return response.data;
  },

  placeLiveParlay: async (data) => {
    const response = await axios.post(`${API_BASE}/live/parlay`, data);
    return response.data;
  },

  getLiveBets: async () => {
    const response = await axios.get(`${API_BASE}/live/bets`);
    return response.data;
  },

  getLiveParlays: async () => {
    const response = await axios.get(`${API_BASE}/live/parlays`);
    return response.data;
  },

  // Customer Service API
  cs: {
    searchCustomers: async (q) => {
      const response = await axios.get(`${API_BASE}/cs/customers/search?q=${encodeURIComponent(q)}`);
      return response.data;
    },
    getCustomer: async (id) => {
      const response = await axios.get(`${API_BASE}/cs/customers/${id}`);
      return response.data;
    },
    voidTicket: async (ticketNumber) => {
      const response = await axios.post(`${API_BASE}/cs/tickets/${ticketNumber}/void`);
      return response.data;
    },
    settleTicket: async (ticketNumber, outcome) => {
      const response = await axios.post(`${API_BASE}/cs/tickets/${ticketNumber}/settle`, { outcome });
      return response.data;
    },
    adjustBalance: async (customerId, amount, type, reason) => {
      const response = await axios.post(`${API_BASE}/cs/customers/${customerId}/adjust`, { amount, type, reason });
      return response.data;
    },
  },

  // Rules & FAQ
  getRules: async () => {
    const response = await axios.get(`${API_BASE}/rules`);
    return response.data;
  },

  createRule: async (data) => {
    const response = await axios.post(`${API_BASE}/rules`, data);
    return response.data;
  },

  updateRule: async (id, data) => {
    const response = await axios.put(`${API_BASE}/rules/${id}`, data);
    return response.data;
  },

  deleteRule: async (id) => {
    const response = await axios.delete(`${API_BASE}/rules/${id}`);
    return response.data;
  },

  reorderRules: async (items) => {
    const response = await axios.post(`${API_BASE}/rules/reorder`, items);
    return response.data;
  },

  // Casino
  getCasinoGames: async () => {
    const response = await axios.get(`${API_BASE}/casino/games`);
    return response.data;
  },

  launchCasinoGame: async (gameSlug) => {
    const response = await axios.post(`${API_BASE}/casino/launch`, { gameSlug });
    return response.data;
  },

  // Leaderboard
  leaderboard: {
    getCurrent: async () => {
      const response = await axios.get(`${API_BASE}/leaderboard/current`);
      return response.data;
    },
    getLastWeek: async () => {
      const response = await axios.get(`${API_BASE}/leaderboard/last-week`);
      return response.data;
    },
    getCustomerBets: async (customerId) => {
      const response = await axios.get(`${API_BASE}/leaderboard/last-week/customer/${customerId}/bets`);
      return response.data;
    },
  },
};
